import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Avatar,
  MenuItem,
  Typography,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import api, { API_BASE_URL } from "../../utils/api";

const GateVisitorDetailsModal = ({
  visitor,
  open,
  onClose,
  onSave,
  onChange,
  onUploadImage,
  visitorTypes = [],
  visitorStatuses = [],
  onAfterSave,
  saveError = "",
}) => {
  const [preview, setPreview] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setImageError(false);
    }
  }, [open]);

  if (!visitor) return null;

  const initials = `${visitor.FirstName?.[0] || ""}${visitor.LastName?.[0] || ""}`.toUpperCase();
  const photoUrl = visitor.PicturePath
    ? `${API_BASE_URL}/gate-visitor-images/${visitor.PicturePath}`
    : null;

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("visitId", visitor.VisitID);

        const res = await api.post("/upload-gate-visitor-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data?.filename) {
          onChange("PicturePath", res.data.filename);
        }
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          pt: 3,
          pb: 1,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.35rem",
                color: "#1f1f1f",
              }}
            >
              Edit Gate Visitor
            </Typography>
            <Typography
              sx={{
                color: "#6b778c",
                fontSize: "0.92rem",
                mt: 0.25,
              }}
            >
              Visitor #{visitor.VisitID}
            </Typography>
          </Box>

          <IconButton
            edge="end"
            onClick={onClose}
            sx={{
              borderRadius: "10px",
              backgroundColor: "rgba(0,0,0,0.04)",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.03)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Avatar
              src={preview || (!imageError ? photoUrl : undefined)}
              sx={{
                width: 120,
                height: 120,
                fontSize: 40,
                fontWeight: 700,
                backgroundColor: "#2f63ad",
                boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                mb: 2,
              }}
              onError={() => setImageError(true)}
            >
              {initials}
            </Avatar>

            <Button
              variant="outlined"
              component="label"
              startIcon={<PhotoCamera />}
              sx={secondaryButtonSx}
            >
              Upload New Photo
              <input type="file" accept="image/*" hidden onChange={handleFileChange} />
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Visitor First Name"
              fullWidth
              value={visitor.FirstName || ""}
              onChange={(e) => onChange("FirstName", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Visitor Last Name"
              fullWidth
              value={visitor.LastName || ""}
              onChange={(e) => onChange("LastName", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Arrival Date"
              type="datetime-local"
              fullWidth
              value={visitor.ArrivalDate ? visitor.ArrivalDate.slice(0, 16) : ""}
              onChange={(e) => onChange("ArrivalDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Departure Date"
              type="datetime-local"
              fullWidth
              value={visitor.DepartureDate ? visitor.DepartureDate.slice(0, 16) : ""}
              onChange={(e) => onChange("DepartureDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              value={visitor.Description || ""}
              onChange={(e) => onChange("Description", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Vehicle Plate"
              fullWidth
              value={visitor.VehiclePlate || ""}
              onChange={(e) => onChange("VehiclePlate", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Vehicle Type"
              fullWidth
              value={visitor.VehicleType || ""}
              onChange={(e) => onChange("VehicleType", e.target.value)}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Visitor Type"
              select
              fullWidth
              value={visitor.VisitorTypeID || ""}
              onChange={(e) => onChange("VisitorTypeID", Number(e.target.value))}
              sx={fieldSx}
            >
              {visitorTypes.map((type) => (
                <MenuItem key={type.TypeID} value={type.TypeID}>
                  {type.TypeName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Status"
              select
              fullWidth
              value={visitor.StatusID || ""}
              onChange={(e) => onChange("StatusID", Number(e.target.value))}
              sx={fieldSx}
            >
              {visitorStatuses.map((status) => (
                <MenuItem key={status.StatusID} value={status.StatusID}>
                  {status.StatusName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={visitor.Notes || ""}
              onChange={(e) => onChange("Notes", e.target.value)}
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {saveError && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {saveError}
          </Alert>
        </Box>
      )}

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1.5,
          justifyContent: "center",
          gap: 1.25,
        }}
      >
        <Button onClick={onClose} variant="outlined" sx={secondaryButtonSx}>
          Cancel
        </Button>

        <Button
          onClick={onSave}
          variant="contained"
          sx={primaryButtonSx}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GateVisitorDetailsModal;